/**
 * PDF Export Utility
 * Generates printable HTML documents that can be saved as PDF via browser print dialog
 */

import { applyNecEdition, getActiveNecEdition } from '../engine/tables/necEditions';
import { formatCalculationSteps } from './calculationSheet';
import type { CalculationStep } from '../components/common/CalculationExplainer';

export interface PDFSection {
  title: string;
  content: string;
  type: 'text' | 'table' | 'calculation';
}

export interface PDFExportOptions {
  title: string;
  subtitle?: string;
  projectName?: string;
  preparedFor?: string;
  preparedBy?: string;
  date?: string;
  sections: PDFSection[];
  includeHeader?: boolean;
  includeFooter?: boolean;
  includeNECReferences?: boolean;
  references?: Array<{ table: string; description: string }>;
  /**
   * "Show your work": the page's CalculationExplainer steps. When given, a
   * "How it was calculated" section is appended after `sections` so the
   * printed record carries the rule, the numbers and the citation for every
   * step, not only the inputs and answers.
   */
  calculationSteps?: CalculationStep[];
}

/** Escape user-controlled text interpolated into the print HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Generate a printable HTML document and open print dialog.
 *
 * Scalar fields (title/subtitle/project/prepared-by/section titles) are
 * user-controlled — a project or control group named "<img onerror=…>" must
 * render as text, so they are HTML-escaped here. `section.content` is the
 * ONE intentional HTML slot; callers build it with their own escaping.
 */
export function generatePDFDocument(options: PDFExportOptions): void {
  const {
    subtitle: rawSubtitle,
    projectName: rawProjectName,
    preparedFor: rawPreparedFor,
    preparedBy: rawPreparedBy,
    date: rawDate,
    includeHeader = true,
    includeFooter = true,
    includeNECReferences = true,
  } = options;
  // Citations in the app are NEC 2023; renumber them to the edition selected in Settings.
  const necEdition = getActiveNecEdition();
  const cite = (text: string) => applyNecEdition(text, necEdition);
  const references = (options.references ?? []).map((ref) => ({
    table: cite(ref.table),
    description: cite(ref.description),
  }));
  const title = escapeHtml(options.title);
  const subtitle = rawSubtitle === undefined ? undefined : escapeHtml(rawSubtitle);
  const projectName = rawProjectName === undefined ? undefined : escapeHtml(rawProjectName);
  const preparedFor = rawPreparedFor === undefined ? undefined : escapeHtml(rawPreparedFor);
  const preparedBy = rawPreparedBy === undefined ? undefined : escapeHtml(rawPreparedBy);
  const date = escapeHtml(rawDate ?? new Date().toLocaleDateString());
  const allSections: PDFSection[] = options.calculationSteps && options.calculationSteps.length > 0
    ? [...options.sections, { title: 'How it was calculated', content: formatCalculationSteps(options.calculationSteps), type: 'calculation' }]
    : options.sections;
  const sections = allSections.map((s) => ({
    ...s,
    title: cite(escapeHtml(s.title)),
    content: cite(s.content),
  }));

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    @page {
      size: letter;
      margin: 0.75in;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11pt;
      line-height: 1.5;
      color: #1f2937;
      background: white;
    }

    .document {
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0.5in;
    }

    @media print {
      .document {
        padding: 0;
      }
    }

    /* Header */
    .doc-header {
      border-bottom: 2px solid #3b82f6;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }

    .doc-title {
      font-size: 20pt;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 4px;
    }

    .doc-subtitle {
      font-size: 12pt;
      color: #6b7280;
    }

    .doc-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      margin-top: 16px;
      font-size: 10pt;
      color: #4b5563;
    }

    .doc-meta-item {
      display: flex;
      flex-direction: column;
    }

    .doc-meta-label {
      font-size: 8pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #9ca3af;
    }

    .doc-meta-value {
      font-weight: 500;
    }

    /* Sections */
    .section {
      margin-bottom: 24px;
      page-break-inside: avoid;
    }

    .section-title {
      font-size: 12pt;
      font-weight: 600;
      color: #3b82f6;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }

    .section-content {
      font-size: 10pt;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;
      font-size: 10pt;
    }

    th, td {
      padding: 8px 12px;
      text-align: left;
      border: 1px solid #e5e7eb;
    }

    th {
      background: #f3f4f6;
      font-weight: 600;
      color: #374151;
    }

    /* "How it was calculated" sheet */
    .calc-sheet { margin: 0 0 0 1.25em; padding: 0; }
    .calc-sheet-step { margin: 0 0 10px; page-break-inside: avoid; }
    .calc-sheet-label { font-weight: 600; color: #1f2937; }
    .calc-sheet-formula { font-family: 'Courier New', monospace; font-size: 10pt; color: #374151; margin-top: 2px; }
    .calc-sheet-value { font-weight: 600; margin-top: 2px; }
    .calc-sheet-ref { font-size: 9pt; color: #6b7280; margin-top: 2px; }
    .calc-sheet-details { width: auto; margin: 4px 0 0; font-size: 9.5pt; }
    .calc-sheet-details td { padding: 2px 8px; border: none; }
    .calc-sheet-details tr.current td { font-weight: 600; }

    tr:nth-child(even) {
      background: #f9fafb;
    }

    /* Calculation blocks */
    .calculation {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: 12px 16px;
      margin: 8px 0;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-size: 10pt;
    }

    .calc-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px dashed #e2e8f0;
    }

    .calc-row:last-child {
      border-bottom: none;
    }

    .calc-row.result {
      font-weight: 600;
      color: #3b82f6;
      border-top: 2px solid #3b82f6;
      margin-top: 8px;
      padding-top: 8px;
    }

    /* References */
    .references {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
    }

    .references-title {
      font-size: 10pt;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 8px;
    }

    .reference-item {
      font-size: 9pt;
      padding: 4px 0;
      color: #4b5563;
    }

    .reference-item strong {
      color: #3b82f6;
    }

    /* Footer */
    .doc-footer {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
      font-size: 8pt;
      color: #9ca3af;
      text-align: center;
    }

    /* Print button (hidden in print) */
    .print-controls {
      position: fixed;
      bottom: 24px;
      right: 24px;
      display: flex;
      gap: 8px;
    }

    @media print {
      .print-controls {
        display: none;
      }
    }

    .btn {
      padding: 10px 20px;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-primary {
      background: #3b82f6;
      color: white;
    }

    .btn-primary:hover {
      background: #2563eb;
    }

    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
    }

    .btn-secondary:hover {
      background: #e5e7eb;
    }
  </style>
</head>
<body>
  <div class="document">
    ${includeHeader ? `
    <header class="doc-header">
      <h1 class="doc-title">${title}</h1>
      ${subtitle ? `<p class="doc-subtitle">${subtitle}</p>` : ''}
      <div class="doc-meta">
        ${projectName ? `
        <div class="doc-meta-item">
          <span class="doc-meta-label">Project</span>
          <span class="doc-meta-value">${projectName}</span>
        </div>
        ` : ''}
        ${preparedFor ? `
        <div class="doc-meta-item">
          <span class="doc-meta-label">Prepared For</span>
          <span class="doc-meta-value">${preparedFor}</span>
        </div>
        ` : ''}
        ${preparedBy ? `
        <div class="doc-meta-item">
          <span class="doc-meta-label">Prepared By</span>
          <span class="doc-meta-value">${preparedBy}</span>
        </div>
        ` : ''}
        <div class="doc-meta-item">
          <span class="doc-meta-label">Date</span>
          <span class="doc-meta-value">${date}</span>
        </div>
      </div>
    </header>
    ` : ''}

    <main>
      ${sections.map(section => `
      <section class="section">
        <h2 class="section-title">${section.title}</h2>
        <div class="section-content ${section.type}">
          ${section.content}
        </div>
      </section>
      `).join('')}

      ${includeNECReferences && references.length > 0 ? `
      <div class="references">
        <h3 class="references-title">NEC ${necEdition} Code References</h3>
        ${references.map(ref => `
        <div class="reference-item">
          <strong>${ref.table}</strong> - ${ref.description}
        </div>
        `).join('')}
      </div>
      ` : ''}
    </main>

    ${includeFooter ? `
    <footer class="doc-footer">
      Generated by BGD Electrical Calculator | ${date}
      <br>
      Calculations should be verified by a licensed professional engineer.
    </footer>
    ` : ''}
  </div>

  <div class="print-controls">
    <button class="btn btn-secondary" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print / Save PDF</button>
  </div>
</body>
</html>
  `.trim();

  // Open in new window for printing
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

/**
 * Format calculation data as HTML table rows
 */
export function formatCalculationTable(data: Array<{ label: string; value: string; unit?: string }>): string {
  return `
    <table>
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Value</th>
          <th>Unit</th>
        </tr>
      </thead>
      <tbody>
        ${data.map(row => `
        <tr>
          <td>${row.label}</td>
          <td>${row.value}</td>
          <td>${row.unit || ''}</td>
        </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Format calculation breakdown as styled HTML
 */
export function formatCalculationBreakdown(steps: Array<{ label: string; value: string; isResult?: boolean }>): string {
  return `
    <div class="calculation">
      ${steps.map(step => `
      <div class="calc-row${step.isResult ? ' result' : ''}">
        <span>${step.label}</span>
        <span>${step.value}</span>
      </div>
      `).join('')}
    </div>
  `;
}

export default generatePDFDocument;
